// src/settings/AccountPanel.tsx
import { useState, type FormEvent } from 'react'
import { useSession, useLogout, useChangePassword } from '../auth/useAuth'

const fieldStyle = {
  background: 'var(--elevated)',
  border: '1px solid var(--line)',
  borderRadius: '8px',
  padding: '0.5rem 0.75rem',
  color: 'var(--fg)',
  fontSize: '0.9375rem',
  outline: 'none',
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box' as const,
}

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  color: 'var(--muted)',
  fontWeight: 500,
  margin: '0 0 0.25rem',
}

function ChangePasswordForm() {
  const change = useChangePassword()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLocalError(null)
    setDone(false)
    if (next.length < 12) {
      setLocalError('Le nouveau mot de passe doit faire au moins 12 caractères.')
      return
    }
    if (next !== confirm) {
      setLocalError('La confirmation ne correspond pas.')
      return
    }
    if (next === current) {
      setLocalError('Le nouveau mot de passe doit être différent de l’actuel.')
      return
    }
    change.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          setCurrent('')
          setNext('')
          setConfirm('')
          setDone(true)
        },
      },
    )
  }

  const errorMessage = localError ?? (change.isError ? change.error.message : null)

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <p style={{ color: 'var(--fg)', fontSize: '0.875rem', fontWeight: 600, margin: 0 }}>
        Changer le mot de passe
      </p>

      <div>
        <label htmlFor="current-password" style={labelStyle}>Mot de passe actuel</label>
        <input id="current-password" type="password" autoComplete="current-password" required
          value={current} onChange={(e) => setCurrent(e.target.value)} style={fieldStyle} />
      </div>
      <div>
        <label htmlFor="new-password" style={labelStyle}>Nouveau mot de passe (12 caractères min.)</label>
        <input id="new-password" type="password" autoComplete="new-password" required minLength={12}
          value={next} onChange={(e) => setNext(e.target.value)} style={fieldStyle} />
      </div>
      <div>
        <label htmlFor="confirm-password" style={labelStyle}>Confirmer le nouveau mot de passe</label>
        <input id="confirm-password" type="password" autoComplete="new-password" required
          value={confirm} onChange={(e) => setConfirm(e.target.value)} style={fieldStyle} />
      </div>

      {errorMessage && (
        <p role="alert" style={{ color: 'var(--arancio)', fontSize: '0.8125rem', margin: 0 }}>
          {errorMessage}
        </p>
      )}
      {done && (
        <p role="status" style={{ color: 'var(--bronze)', fontSize: '0.8125rem', margin: 0 }}>
          Mot de passe mis à jour. Il sera demandé à ta prochaine connexion.
        </p>
      )}

      <button type="submit" disabled={change.isPending}
        style={{
          alignSelf: 'flex-start',
          background: 'var(--arancio)',
          color: '#fff',
          border: 'none',
          borderRadius: '7px',
          padding: '0.5rem 1rem',
          fontSize: '0.875rem',
          fontWeight: 600,
          cursor: change.isPending ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
        }}>
        {change.isPending ? 'Mise à jour…' : 'Mettre à jour le mot de passe'}
      </button>
    </form>
  )
}

export function AccountPanel() {
  const { data } = useSession()
  const logout = useLogout()

  return (
    <section style={{
      background: 'var(--surface)',
      border: '1px solid var(--line)',
      borderRadius: '12px',
      padding: '1.5rem',
    }}>
      <h2 style={{
        fontSize: '1rem',
        fontWeight: 600,
        color: 'var(--fg)',
        margin: '0 0 1.25rem',
        letterSpacing: '-0.015em',
      }}>
        Compte
      </h2>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}>
        <div style={{
          padding: '0.75rem',
          background: 'var(--elevated)',
          borderRadius: '8px',
          border: '1px solid var(--line)',
        }}>
          <p style={{ color: 'var(--dim)', fontSize: '0.75rem', margin: '0 0 0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Adresse e-mail
          </p>
          <p style={{ color: 'var(--fg)', fontSize: '0.9375rem', margin: 0 }}>
            {data?.user.email ?? '—'}
          </p>
        </div>

        <div style={{
          padding: '0.75rem',
          background: 'var(--elevated)',
          borderRadius: '8px',
          border: '1px solid var(--line)',
        }}>
          <p style={{ color: 'var(--dim)', fontSize: '0.75rem', margin: '0 0 0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Rôle
          </p>
          <p style={{ color: 'var(--fg)', fontSize: '0.9375rem', margin: 0, textTransform: 'capitalize' }}>
            {data?.user.role ?? '—'}
          </p>
        </div>

        <div style={{
          borderTop: '1px solid var(--line)',
          paddingTop: '1rem',
        }}>
          <ChangePasswordForm />
        </div>

        <div style={{
          borderTop: '1px solid var(--line)',
          paddingTop: '1rem',
        }}>
          <button
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            style={{
              alignSelf: 'flex-start',
              background: 'transparent',
              border: '1px solid rgba(217,83,30,0.4)',
              borderRadius: '7px',
              padding: '0.4375rem 0.875rem',
              color: 'var(--arancio)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {logout.isPending ? 'Déconnexion…' : 'Déconnecter toutes les sessions'}
          </button>
        </div>
      </div>
    </section>
  )
}
